import { groupByCodeAndStep, groupHeading } from "@/lib/migrations/residue";
import type { EngineResidueItem } from "@/services/migrations/engine";

/**
 * The residue list — every source construct the target cannot express, with the manual
 * step that recovers it.
 *
 * Grouped by `(code, manual_step)`, a direct port of the engine CLI's `_grouped`
 * (ncoder-playground `app/migration_engine/runbook.py`). One real Profile run produced
 * 38 near-identical `UNKNOWN_TAB` entries; printing that manual step 38 times does not
 * make the report more honest, it makes the other three entries unfindable. The step is
 * printed once with a count, and EVERY source ref is still listed beneath it — grouping
 * changes the rendering, never the data.
 *
 * IT RENDERS RESIDUE AND ONLY RESIDUE. It used to also list `dropped_with_reason` and
 * `unrepresentable`; those are coverage BUCKETS and they now page through `/effects`
 * under the coverage panel, where their counts are. Listing them twice made the same
 * loss look like two losses and put an unpaged copy of a paged list on the screen.
 *
 * FIELD NAMES ARE THE ENGINE'S. `source_ref` points INTO THE SOURCE ("Account.Rating",
 * "loginIpRanges") and `detail` is the explanation; residue has no `target` and no
 * `strength`, because there is nothing honest to put in them.
 */
export function ResiduePanel({ residue }: { residue: EngineResidueItem[] }) {
  if (residue.length === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        No residue: nothing in the source needed a manual step.
      </p>
    );
  }
  const groups = groupByCodeAndStep(residue);
  const total = groups.reduce((sum, group) => sum + group.count, 0);

  return (
    <section className="space-y-3">
      <div>
        <h4 className="font-mono text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Manual steps ({total})
        </h4>
        <p className="mt-0.5 max-w-prose text-xs text-muted-foreground">
          Source constructs the target cannot express. These are not optional and nothing else
          will do them — the migration is not finished until they are.
        </p>
      </div>
      <ul className="space-y-3">
        {groups.map((group) => (
          <li
            key={`${group.code}-${group.manual_step}`}
            className="rounded-lg border border-border bg-muted/20 p-3"
          >
            <div className="font-mono text-xs font-semibold text-foreground">
              {groupHeading(group)}
            </div>
            {/* Printed ONCE per group — the whole point of the grouping. */}
            {group.manual_step ? (
              <p className="mt-1 max-w-prose text-xs text-[hsl(var(--accent-warm))]">
                <span className="font-mono uppercase">manual step: </span>
                {group.manual_step}
              </p>
            ) : (
              <p className="mt-1 text-xs text-destructive">
                No manual step was declared for this group.
              </p>
            )}
            {/* …and every source ref still listed individually underneath it. */}
            <ul className="mt-2 space-y-0.5">
              {group.items.map((item, index) => (
                <li
                  key={`${item.code}-${item.source_ref}-${index}`}
                  className="break-words font-mono text-[11px] text-muted-foreground"
                >
                  {item.source_ref}
                  {item.detail ? `: ${item.detail}` : ""}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}
